import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { ExternalLinkIcon, RepeatIcon } from "@chakra-ui/icons";
import {
  cancelBillingSubscription,
  createManageSubscriptionSession,
  getBillingAttention,
  getSession,
} from "./api";
import { BillingAttentionItem, User } from "./types";
import { AppContext } from "./context";
import { isAllAccessType } from "./allAccess";
import { PRICING, TIER_INFO } from "./pricingData";

const productName = (item: BillingAttentionItem) =>
  isAllAccessType(item.type)
    ? "ALL-ACCESS"
    : PRICING[item.type]?.displayName || item.type.toUpperCase();
const planName = (item: BillingAttentionItem) =>
  isAllAccessType(item.type)
    ? "Subscription"
    : item.type.startsWith("stories_")
    ? "Subscription"
    : TIER_INFO[item.tier].name;
const accessName = (item: BillingAttentionItem, user: User) => {
  const allAccess =
    !item.type.startsWith("stories_") &&
    user.subscriptions.some(
      (s) => isAllAccessType(s.type) && s.tier === "paid+"
    );
  if (allAccess) return "ALL-ACCESS";
  return TIER_INFO[
    user.subscriptions.find((s) => s.type === item.type)?.tier || "free"
  ].name;
};
const money = (amount: number, currency: string) => {
  const format = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });
  return format.format(
    amount / Math.pow(10, format.resolvedOptions().maximumFractionDigits)
  );
};
const endDate = (item: BillingAttentionItem) =>
  item.currentPeriodEnd
    ? new Date(item.currentPeriodEnd).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "the end of the billing period";

export const BillingAttention = ({
  onRefresh,
  onTypesChange,
  onChoosePlan,
}: {
  onRefresh: () => Promise<void>;
  onChoosePlan: (type: BillingAttentionItem["type"]) => void;
  onTypesChange?: (types: BillingAttentionItem["type"][]) => void;
}) => {
  const { user, setUser } = React.useContext(AppContext);
  const [items, setItems] = React.useState<BillingAttentionItem[] | null>(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [refreshing, setRefreshing] = React.useState(false);
  const [selected, setSelected] = React.useState<BillingAttentionItem | null>(
    null
  );
  const [canceling, setCanceling] = React.useState(false);
  const [cancelError, setCancelError] = React.useState("");
  const [portalLoading, setPortalLoading] = React.useState(false);
  const mounted = React.useRef(true);
  const requestId = React.useRef(0);
  const bg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const muted = useColorModeValue("gray.600", "gray.400");

  const stripeCustomerId = user?.stripeCustomerId;
  const load = React.useCallback(async () => {
    const id = ++requestId.current;
    if (!stripeCustomerId) {
      setItems([]);
      setError("");
      setNotice("");
      setSelected(null);
      setRefreshing(false);
      onTypesChange?.([]);
      return false;
    }
    setRefreshing(true);
    try {
      const result = await getBillingAttention();
      if (mounted.current && id === requestId.current) {
        setItems(result);
        onTypesChange?.(
          result.filter((item) => item.purchase).map((item) => item.type)
        );
        setError("");
        return true;
      }
      return false;
    } catch {
      if (mounted.current && id === requestId.current)
        setError("Billing details are unavailable. Please try again.");
      return false;
    } finally {
      if (mounted.current && id === requestId.current) setRefreshing(false);
    }
  }, [onTypesChange, stripeCustomerId]);
  const refresh = React.useCallback(async () => {
    const ok = await load();
    if (ok) {
      try {
        const fresh = await getSession();
        if (mounted.current) setUser(fresh);
        await onRefresh();
      } catch {
        if (mounted.current)
          setError(
            "Billing refreshed. Account details could not be refreshed. Please try again."
          );
      }
    }
  }, [load, setUser, onRefresh]);
  React.useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);
  React.useEffect(() => {
    const onFocus = () => {
      if (!selected && !canceling) void refresh();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) onFocus();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [refresh, selected, canceling]);

  const cancel = async () => {
    if (!selected) return;
    setCanceling(true);
    setCancelError("");
    try {
      const result = await cancelBillingSubscription(selected);
      setNotice(
        result.reverted
          ? "Trial ended. Your previous plan is being restored."
          : result.scheduled
          ? "Cancellation scheduled. Your subscription will not renew."
          : "Subscription canceled. It will not renew."
      );
      setSelected(null);
      await refresh();
    } catch (e: any) {
      setCancelError(
        e.response?.data?.error ||
          "We couldn’t confirm cancellation. Refresh billing details before trying again."
      );
    } finally {
      setCanceling(false);
    }
  };
  const paymentDetails = async () => {
    setPortalLoading(true);
    try {
      window.location.assign(await createManageSubscriptionSession());
    } catch {
      setError("Couldn’t open payment settings. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  if (!user || !stripeCustomerId) return null;
  // Do not suggest a billing problem before the first request confirms one.
  if (!items?.length && !error && !notice) return null;
  return (
    <Box
      role={items?.length ? "region" : undefined}
      aria-label={items?.length ? "Billing Attention" : undefined}
    >
      {!!items?.length && (
        <Flex justify="space-between" align="center" mb={3} gap={3}>
          <Heading size="sm" fontWeight="600">
            Billing Attention
          </Heading>
          <Button
            size="xs"
            variant="ghost"
            leftIcon={<RepeatIcon />}
            onClick={() => void refresh()}
            isLoading={refreshing}
            loadingText="Refreshing"
          >
            Refresh
          </Button>
        </Flex>
      )}
      <VStack align="stretch" spacing={3}>
        {notice && (
          <Box
            role="status"
            borderWidth="1px"
            borderColor={border}
            bg={bg}
            borderRadius="lg"
            p={4}
          >
            <Text fontSize="sm">{notice}</Text>
          </Box>
        )}
        {error && (
          <Flex
            role="alert"
            borderWidth="1px"
            borderColor={border}
            bg={bg}
            borderRadius="lg"
            p={4}
            gap={3}
            align="center"
            justify="space-between"
            flexWrap="wrap"
          >
            <Text fontSize="sm">{error}</Text>
            <Button
              size="sm"
              variant="outline"
              ml="auto"
              onClick={() => void refresh()}
            >
              Retry
            </Button>
          </Flex>
        )}
        {items?.map((item, index) => (
          <Box
            key={item.token}
            data-testid={`billing-item-${index}`}
            bg={bg}
            borderWidth="1px"
            borderColor={border}
            borderRadius="xl"
            overflow="hidden"
          >
            <Box p={{ base: 4, md: 5 }}>
              <Flex
                justify="space-between"
                gap={3}
                align="start"
                flexWrap="wrap"
              >
                <Flex align="baseline" gap={2} flexWrap="wrap">
                  <Heading size="sm">{productName(item)}</Heading>
                  <Text fontSize="sm" color={muted}>
                    {planName(item)}
                  </Text>
                </Flex>
                <Badge
                  colorScheme="gray"
                  variant="subtle"
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontWeight="500"
                  textTransform="none"
                >
                  Current access: {accessName(item, user)}
                </Badge>
              </Flex>
              {item.purchase ? (
                <Box mt={4}>
                  <Text fontSize="lg" fontWeight="600">
                    {money(item.purchase.amount, item.purchase.currency)}/
                    {item.purchase.interval === "year" ? "year" : "month"}
                  </Text>
                  <Text fontSize="sm" color={muted} mt={1}>
                    Resubscribe to start a new billing period.
                  </Text>
                </Box>
              ) : (
                <Text fontSize="sm" color={muted} mt={4}>
                  This plan is unavailable for resubscription. Choose a current
                  plan.
                </Text>
              )}
              {item.cancelAtPeriodEnd && (
                <Text fontSize="sm" color={muted} mt={2}>
                  Cancellation scheduled for {endDate(item)}.
                </Text>
              )}
              {item.billingLocked && (
                <Text fontSize="sm" color={muted} mt={2}>
                  Finish managing your trial before changing this subscription.
                </Text>
              )}
            </Box>
            <Flex
              px={{ base: 4, md: 5 }}
              py={3}
              borderTopWidth="1px"
              borderColor={border}
              justify="flex-end"
              gap={2}
              flexWrap="wrap"
            >
              <Flex width={{ base: "100%", sm: "auto" }} justify="flex-end">
                <Button
                  size="sm"
                  variant="ghost"
                  rightIcon={<ExternalLinkIcon />}
                  onClick={paymentDetails}
                  isLoading={portalLoading}
                  loadingText="Opening"
                >
                  Update payment method
                </Button>
              </Flex>
              {item.cancellationMode && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelected(item);
                    setCancelError("");
                  }}
                >
                  Cancel subscription
                </Button>
              )}
              {item.purchase ? (
                <Button
                  as={RouterLink}
                  size="sm"
                  colorScheme="brand"
                  to={`/checkout?tier=${
                    item.purchase.tier === "paid+"
                      ? "paid_plus"
                      : item.purchase.tier
                  }&type=${item.purchase.type}&billing_interval=${
                    item.purchase.interval
                  }`}
                >
                  Resubscribe
                </Button>
              ) : (
                <Button
                  size="sm"
                  colorScheme="brand"
                  onClick={() =>
                    onChoosePlan(
                      isAllAccessType(item.type) ? "all_access_v3" : item.type
                    )
                  }
                >
                  Choose plan
                </Button>
              )}
            </Flex>
          </Box>
        ))}
      </VStack>
      <Modal
        isOpen={!!selected}
        onClose={() => {
          if (!canceling) setSelected(null);
        }}
        isCentered
        closeOnOverlayClick={!canceling}
        closeOnEsc={!canceling}
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent mx={4} borderRadius="xl">
          <ModalHeader>
            Cancel {selected ? productName(selected) : "subscription"}?
          </ModalHeader>
          <ModalCloseButton isDisabled={canceling} />
          <ModalBody>
            <Text fontSize="sm">
              {selected?.cancellationMode === "period_end"
                ? `Your subscription will end on ${endDate(
                    selected
                  )} and will not renew.`
                : selected?.cancellationMode === "revert_trial"
                ? "Your trial will end now and your previous plan will be restored."
                : "Your subscription will end immediately and will not renew."}
            </Text>
            {cancelError && (
              <Text role="alert" color="red.400" fontSize="sm" mt={3}>
                {cancelError}
              </Text>
            )}
          </ModalBody>
          <ModalFooter gap={3} flexWrap="wrap">
            {cancelError && (
              <Button
                variant="outline"
                onClick={async () => {
                  setSelected(null);
                  await refresh();
                }}
              >
                Refresh billing
              </Button>
            )}
            <Button
              variant="ghost"
              isDisabled={canceling}
              onClick={() => setSelected(null)}
            >
              Keep subscription
            </Button>
            <Button colorScheme="red" isLoading={canceling} onClick={cancel}>
              Confirm cancellation
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
